'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  OPEN_PREFERENCES_EVENT,
  countryNeedsConsent,
  currentGoogleConsent,
  gpcOn,
  pushConsentUpdate,
  readChoice,
  saveChoice,
  timezoneNeedsConsent,
} from '@/lib/consent';

/**
 * The cookie preferences card.
 *
 * Everyone sees it once, on their first visit, until they choose. Three
 * groups, each mapped to something the site really does:
 *
 *   Essential  always on: security, remembering this choice, the forms you send.
 *   Analytics  Google Analytics, the site's own visit counts, heatmaps and A/B
 *              tests (they share one switch: see lib/visitor-id.ts).
 *   Marketing  Google Ads conversion measurement. No remarketing.
 *
 * "Accept all" is the large button; "Customize" and "Reject optional" sit
 * beside it as quieter buttons, always visible, one click each. In the EEA,
 * the UK and Switzerland nothing optional runs until the visitor accepts, and
 * the switches under Customize start off. Elsewhere analytics runs by default
 * (opt-out) and the switches start on. "Privacy choices" in the footer
 * reopens it at Customize.
 *
 * It never blocks the page: it sits at the bottom left, away from the assistant button.
 */

type View = 'ask' | 'customize';

export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('ask');
  const [needs, setNeeds] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const first = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  // Where is the visitor? Time zone straight away, then the server's answer, which wins when it knows.
  useEffect(() => {
    let tz = '';
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      /* ignore */
    }
    let needsConsent = timezoneNeedsConsent(tz);
    setGpc(gpcOn());
    const stored = readChoice();
    setNeeds(needsConsent);
    setAnalytics(stored ? stored.analytics : !needsConsent);
    setAds(stored ? stored.ads : !needsConsent);
    // Nothing chosen yet: ask, after a moment, so the page is seen first.
    const show = stored ? null : setTimeout(() => setOpen(true), 1200);

    let alive = true;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 2500);

    const apply = (country: string | null) => {
      if (!alive || !country) return;
      needsConsent = countryNeedsConsent(country);
      setNeeds(needsConsent);
      if (readChoice()) return;
      // No choice yet: apply the state for where the visitor really is.
      pushConsentUpdate(currentGoogleConsent(needsConsent));
      setAnalytics(!needsConsent);
      setAds(!needsConsent);
    };

    // A stored choice settles it, so there is nothing to look up. Otherwise ask the server once per browser
    // session (a function call per page view would be wasteful) and remember the answer for 30 minutes.
    let cached: string | null = null;
    try {
      const c = JSON.parse(sessionStorage.getItem('kit_region') || 'null') as { country?: string; at?: number } | null;
      if (c?.country && typeof c.at === 'number' && Date.now() - c.at < 30 * 60 * 1000) cached = c.country;
    } catch {
      /* ignore */
    }
    if (stored) {
      clearTimeout(timer);
    } else if (cached) {
      clearTimeout(timer);
      apply(cached);
    } else {
      fetch('/api/region', { signal: ctl.signal, cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((geo: { country: string | null } | null) => {
          if (!geo?.country) return;
          try {
            sessionStorage.setItem('kit_region', JSON.stringify({ country: geo.country, at: Date.now() }));
          } catch {
            /* ignore */
          }
          apply(geo.country);
        })
        .catch(() => {
          /* offline or blocked: the time zone answer stands */
        })
        .finally(() => clearTimeout(timer));
    }
    return () => {
      alive = false;
      ctl.abort();
      clearTimeout(timer);
      if (show) clearTimeout(show);
    };
  }, []);

  // "Privacy choices" in the footer: straight to the switches, showing what is on now.
  useEffect(() => {
    const onOpen = () => {
      returnFocus.current = document.activeElement as HTMLElement | null;
      const c = currentGoogleConsent(needs);
      setAnalytics(c.analytics_storage === 'granted');
      setAds(c.ad_storage === 'granted');
      setGpc(gpcOn());
      setView('customize');
      setOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpen);
  }, [needs]);

  useEffect(() => {
    if (open && returnFocus.current) first.current?.focus();
  }, [open, view]);

  const save = useCallback(
    (a: boolean, m: boolean) => {
      saveChoice(a, m);
      pushConsentUpdate(currentGoogleConsent(needs));
      setOpen(false);
      returnFocus.current?.focus();
      returnFocus.current = null;
    },
    [needs],
  );

  if (!open) return null;

  return (
    <section
      aria-label="Cookie preferences"
      className="pop-in fixed bottom-3 left-3 right-3 z-[60] max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-[22px] border border-line-2 bg-ink-900 p-5 shadow-[0_30px_80px_-20px_rgb(9_9_11/0.45)] sm:left-4 sm:right-auto sm:w-[26rem] sm:p-6"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-signal text-on-signal">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3a9 9 0 1 0 9 9 4 4 0 0 1-5-5 4 4 0 0 1-4-4Z" />
            <path d="M8.5 11.5h.01 M12 16h.01 M15.5 13.5h.01 M9 15.5h.01" />
          </svg>
        </span>
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight text-mist-100">Your privacy matters</h2>
          <p className="mt-1 text-sm leading-relaxed text-mist-400">
            {needs
              ? 'We use essential technologies to keep KIT working. With your permission, we also use analytics to see how people use the site and make it better.'
              : 'We use essential technologies to keep KIT working, and analytics to see how people use the site and make it better. You decide what stays on.'}
          </p>
        </div>
      </div>

      {view === 'customize' && (
        <ul className="mt-5 grid gap-2">
          <Row title="Essential" note="Always on" body="Security, remembering this choice, and the forms you send us." />
          <Row
            title="Analytics"
            body="Google Analytics, visit counts, heatmaps and A/B tests, to see what helps visitors."
            on={analytics}
            onChange={setAnalytics}
          />
          <Row
            title="Marketing"
            body={gpc ? 'Your browser sends Global Privacy Control, so this stays off.' : 'Measuring which ads lead to an enquiry. We never follow you around the web.'}
            on={ads && !gpc}
            onChange={setAds}
            disabled={gpc}
          />
        </ul>
      )}

      <div className="mt-5 grid gap-2">
        <button ref={first} type="button" onClick={() => save(true, true)} className="btn btn-signal w-full py-3.5 text-[0.98rem]">
          Accept all
        </button>
        {view === 'ask' ? (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setView('customize')} className="btn btn-ghost min-h-[2.75rem] px-3 text-sm">
              Customize
            </button>
            <button type="button" onClick={() => save(false, false)} className="btn btn-ghost min-h-[2.75rem] px-3 text-sm">
              Reject optional
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => save(analytics, ads && !gpc)} className="btn btn-ghost min-h-[2.75rem] w-full px-3 text-sm">
            Save my choices
          </button>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-mist-500">
        Change this any time under Privacy choices.{' '}
        <Link href="/privacy#8-cookies" className="underline underline-offset-2 hover:text-mist-100">
          Privacy policy
        </Link>
      </p>
    </section>
  );
}

function Row({ title, body, note, on, onChange, disabled }: { title: string; body: string; note?: string; on?: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <li className="flex items-start gap-3 rounded-2xl border border-line p-3.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-mist-100">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-mist-400">{body}</p>
      </div>
      {onChange ? (
        <button
          type="button"
          role="switch"
          aria-checked={!!on}
          aria-label={title}
          disabled={disabled}
          onClick={() => onChange(!on)}
          className={`relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-spark-600' : 'bg-ink-600'}`}
        >
          <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : ''}`} />
        </button>
      ) : (
        <span className="mt-0.5 shrink-0 rounded-full bg-veil-2 px-2.5 py-1 text-xs font-semibold text-mist-400">{note}</span>
      )}
    </li>
  );
}

/** The footer button that reopens the banner. */
export function PrivacyChoicesButton() {
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT))}
      className="text-left text-mist-400 transition-colors hover:text-mist-100"
    >
      Privacy choices
    </button>
  );
}
